import { Seo } from "../components/Seo";
import { BadgeCheck, Clock, Sparkles, BookOpen, Leaf, HeartHandshake } from "lucide-react";

const PORTRAIT = "/brand/laura.webp";
const GROWTH = "https://images.unsplash.com/photo-1458014854819-1a40aa70211c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxuYXR1cmUlMjBjb3Vuc2VsaW5nJTIwZ3Jvd3RofGVufDB8fHx8MTc4NDE2ODQ2Nnww&ixlib=rb-4.1.0&q=85";

const CREDENTIALS = [
  { icon: BadgeCheck, label: "Licensed Clinical Social Worker (LCSW)" },
  { icon: Clock, label: "13+ Years of Clinical Experience" },
  { icon: Sparkles, label: "EMDR Trained Therapist" },
  { icon: BookOpen, label: "Author of Welcome to Adulthood" },
  { icon: Leaf, label: "Founder of Laura Combs Productions" },
  { icon: HeartHandshake, label: "Creating Practical Life Skills Resources for Individuals, Families, and Communities" },
];

export default function About() {
  return (
    <div data-testid="about-page">
      <Seo
        title="About Laura Combs"
        description="Meet Laura Combs, LCSW — for over a decade she has helped people navigate trauma, anxiety, relationships, and life's transitions. Discover the mission behind Laura Combs Productions."
        path="/about"
      />
      <section className="max-w-7xl mx-auto px-6 pt-20 pb-16 grid lg:grid-cols-2 gap-16 items-center">
        <div className="fade-up">
          <span className="text-gold text-sm uppercase tracking-[0.3em]">About</span>
          <h1 className="font-serif text-5xl sm:text-6xl text-forest mt-4 leading-tight">
            Guidance rooted in wisdom &amp; warmth.
          </h1>
          <p className="text-charcoal/70 text-lg mt-8 leading-relaxed">
            Laura Combs Productions helps people build stronger lives through books,
            education, practical tools, and compassionate guidance. Every offering is
            crafted to feel like sitting down with a trusted mentor — someone who believes
            in your capacity to grow.
          </p>
          <p className="text-charcoal/70 mt-4 leading-relaxed">
            We believe growth doesn't have to feel clinical or overwhelming. It can be
            calm, hopeful, and empowering. That's the heart of everything we create.
          </p>
        </div>
        <div className="fade-up rounded-2xl overflow-hidden shadow-lg aspect-[4/5]" style={{ animationDelay: "0.15s" }}>
          <img src={PORTRAIT} alt="Laura Combs, LCSW, founder of Laura Combs Productions" className="w-full h-full object-cover" fetchpriority="high" decoding="async" />
        </div>
      </section>

      <section className="bg-white border-y border-gold/25 py-20">
        <div className="max-w-5xl mx-auto px-6 grid sm:grid-cols-3 gap-10 text-center">
          {[
            { k: "Calm Confidence", v: "We meet you with steadiness, never pressure." },
            { k: "Practical Wisdom", v: "Timeless insight paired with tools you'll actually use." },
            { k: "Compassion First", v: "Encouraging, human, and free of clinical jargon." },
          ].map((item) => (
            <div key={item.k}>
              <h3 className="font-serif text-3xl text-forest mb-3">{item.k}</h3>
              <p className="text-charcoal/60 text-sm leading-relaxed">{item.v}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-16 items-center">
        <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3] order-2 lg:order-1">
          <img src={GROWTH} alt="New growth" className="w-full h-full object-cover" loading="lazy" decoding="async" />
        </div>
        <div className="order-1 lg:order-2">
          <h2 className="font-serif text-4xl sm:text-5xl text-forest leading-tight">
            A philosophy of steady, rooted growth.
          </h2>
          <p className="text-charcoal/70 mt-6 leading-relaxed">
            Like a tree, a strong life grows slowly and from the roots up. Our books,
            courses, and tools are designed to help you tend to your foundations — your
            values, your clarity, your resilience — so that the rest can flourish naturally.
          </p>
        </div>
      </section>

      <section className="bg-white border-y border-gold/25">
        <div className="max-w-[1100px] mx-auto px-6 py-20">
          <div className="text-center max-w-2xl mx-auto">
            <span className="text-gold text-sm uppercase tracking-[0.3em]">Credentials</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-forest leading-tight mt-3">
              Trusted Guidance. Practical Experience.
            </h2>
            <p className="text-charcoal/70 mt-5 leading-relaxed">
              Helping people build stronger lives through education, compassion, and
              real-world experience.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-12 gap-y-8 mt-14">
            {CREDENTIALS.map((c) => (
              <div key={c.label} className="flex items-start gap-4" data-testid={`about-credential-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`}>
                <span className="shrink-0 w-12 h-12 rounded-full bg-ivory border border-gold/40 flex items-center justify-center">
                  <c.icon className="w-5 h-5 text-gold" strokeWidth={1.6} />
                </span>
                <p className="text-charcoal/80 leading-relaxed pt-2.5">{c.label}</p>
              </div>
            ))}
          </div>
          <p className="text-gold text-sm uppercase tracking-[0.2em] text-center mt-14">
            Serving readers, families, educators, and helping professionals.
          </p>
        </div>
      </section>
    </div>
  );
}
